import React, { useEffect, useRef, useState } from 'react';
import './style.css';

interface AppDialogProps {
  visible: boolean;
  onHide: () => void;
  header?: React.ReactNode | React.JSX.Element | string;
  children: React.ReactNode;
  className?: string;
  maskClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  closable?: boolean;
  draggable?: boolean;
  style?: React.CSSProperties;
}

const AppDialog: React.FC<AppDialogProps> = ({
  visible,
  onHide,
  header,
  children,
  className = '',
  maskClassName = '',
  contentClassName = '',
  headerClassName = '',
  closable = true,
  draggable = false,
  style,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(visible);
  const dialogRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const dialogPosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      setIsAnimating(true);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      setIsAnimating(false);
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'auto';
      // Hide after animation completes
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible && closable) {
        onHide();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onHide, closable]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closable) {
      onHide();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!draggable) return;

    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !dialogRef.current) return;

      const deltaX = e.clientX - dragStart.current.x;
      const deltaY = e.clientY - dragStart.current.y;

      dialogPosition.current = {
        x: dialogPosition.current.x + deltaX,
        y: dialogPosition.current.y + deltaY,
      };

      dialogRef.current.style.transform = `translate(${dialogPosition.current.x}px, ${dialogPosition.current.y}px)`;

      dragStart.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  if (!shouldRender) return null;

  const renderHeader = () => {
    if (!header) return null;

    return (
      <div className={`app-dialog-header ${headerClassName}`} onMouseDown={draggable ? handleMouseDown : undefined}>
        <div className="app-dialog-title">{typeof header === 'string' ? <h3>{header}</h3> : header}</div>
        {closable && (
          <button className="app-dialog-close-button" onClick={onHide} type="button" aria-label="Close">
            ×
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`app-dialog-overlay ${maskClassName} ${isAnimating ? 'entering' : 'exiting'}`} onClick={handleOverlayClick}>
      <div ref={dialogRef} className={`app-dialog ${className} ${draggable ? 'draggable' : ''} ${isAnimating ? 'entering' : 'exiting'}`} style={style} onClick={(e) => e.stopPropagation()}>
        {renderHeader()}
        <div className={`app-dialog-content ${contentClassName}`}>{children}</div>
      </div>
    </div>
  );
};

export default AppDialog;
